<template>
  <v-container>
    <v-row class="facility-details" justify="center">
      <ViewHeader :title="viewTitle">
        <ui-button
          class="ml-4 mr-auto"
          @click="goToFacilitiesList"
          outlined
          icon="mdi-chevron-left"
        >
          {{ $t("message.facilityDetails.backToFacilitiesList") }}
        </ui-button>
        <ui-button
          v-if="isOwner ? canManageGmbAccount : canManageExternalGmbAddresses"
          :disabled="isReadonly"
          color="error"
          class="ml-2"
          @click="dialog.removeFacility = true"
          outlined
          icon="icon-trash"
        >
          {{ $t("message.common.removeFacility") }}
        </ui-button>
        <v-menu left bottom offset-y :nudge-top="-12">
          <template #activator="{ on }">
            <v-icon color="primary" class="ml-3" v-on="on">icon-more </v-icon>
          </template>
          <v-list>
            <v-list-item ripple @click="dialog.technicalDetails = true">
              <span>{{
                $t("message.facilityDetails.technicalDetailsDialog.title")
              }}</span>
            </v-list-item>
            <v-list-item ripple @click="dialog.reviewFormLink = true">
              <span>{{ $t("message.facilityDetails.reviewFormLink") }}</span>
            </v-list-item>
          </v-list>
        </v-menu>
      </ViewHeader>
      <v-col>
        <v-row no-gutters justify="space-between">
          <v-card flat class="facility-details__card">
            <v-tabs
              v-model="active"
              slider-color="primary"
              ripple
              class="facility-details__tabs"
            >
              <v-tab
                v-for="tab in tabsWithAccess"
                :key="tab.id"
                :to="tab.route"
                :disabled="isReadonly && tab.disableInReadonly"
                class="facility-details__tab"
              >
                {{ $t(tab.label) }}
                <v-tooltip v-if="tab.hasExternalChanges" top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on" class="has-changes"></span>
                  </template>
                  <span>{{
                    $t("message.externalChanges.hasExternalChanges")
                  }}</span>
                </v-tooltip>
              </v-tab>
            </v-tabs>
            <v-tabs-items
              class="transparent overflow-hidden"
              v-model="active"
              @change="updateRouter($event)"
            >
              <v-row no-gutters justify="center">
                <v-col class="facility-details__wrapper">
                  <transition name="fade" mode="out-in">
                    <router-view class="animation-duration--500"></router-view>
                  </transition>
                  <SectionLoader :status="loading.view" :opacity="1" />
                </v-col>
              </v-row>
            </v-tabs-items>
          </v-card>
          <v-col cols="12" v-if="isOwner && isDetailsRoute">
            <v-card
              flat
              class="facility-details__card facility-details__map-card"
            >
              <div class="facility-details-update-statuses-wrapper">
                <div class="section-title">
                  {{ $t("message.facilityDetails.statusOnMaps") }}
                </div>
                <p class="facility-details-update-statuses-description">
                  {{ $t("message.facilityDetails.statusDescription") }}
                </p>
              </div>
              <div class="facility-details-update-statuses-data-wrapper">
                <div
                  class="facility-details-update-statuses"
                  v-if="integrations && integrations.length > 0"
                >
                  <v-row class="d-none d-md-flex">
                    <v-col cols="3">
                      <p class="update-statuses-header">
                        {{ $t("message.facilityDetails.typeOfMap") }}
                      </p>
                    </v-col>
                    <v-col cols="4">
                      <p class="update-statuses-header">
                        {{ $t("message.facilityDetails.status") }}
                      </p>
                    </v-col>
                    <v-col cols="5">
                      <p class="update-statuses-header">
                        {{ $t("message.facilityDetails.details") }}
                      </p>
                    </v-col>
                  </v-row>
                  <v-row
                    v-for="integration in integrations"
                    :key="integration.type"
                    class="align-center"
                  >
                    <v-col cols="12" md="3">
                      <p class="update-status-data update-status-data--name">
                        {{ integration.catalog }}
                      </p>
                    </v-col>
                    <v-col cols="12" md="4">
                      <p
                        class="facility-details-update-status update-status-data"
                      >
                        <IntegrationStatusIndicator
                          :type="integration.status.type"
                          :is-disabled="!integration.is_enabled"
                          class="mr-4"
                        />
                        {{ integration.status.name }}
                      </p>
                    </v-col>
                    <v-col cols="12" md="5">
                      <p class="update-status-data update-status-data--date">
                        {{ integration.additional_info }}
                      </p>
                    </v-col>
                  </v-row>
                </div>
                <no-data v-else></no-data>
              </div>
              <SectionLoader :status="loading.statuses" :opacity="1" />
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <v-dialog v-model="dialog.removeFacility" max-width="450">
        <v-card class="pa-6">
          <p class="mb-8 text-center">
            {{
              $t(
                "message.companyProfile.listings.confirmationDialog.deleteFacilityConfirm"
              )
            }}
          </p>

          <div class="d-flex justify-center">
            <ui-button
              class="mr-3"
              outlined
              color="primary"
              @click="dialog.removeFacility = false"
            >
              {{ $t("message.common.cancel") }}
            </ui-button>
            <v-spacer></v-spacer>
            <ui-button color="primary" @click="onDelete($route.params.id)">
              {{ $t("message.common.confirmDelete") }}
            </ui-button>
          </div>
        </v-card>
      </v-dialog>

      <TechnicalDetailsDialog
        :is-open="dialog.technicalDetails"
        :location-name="gmbLocationName"
        @close="dialog.technicalDetails = false"
      />
      <ReviewFormLinkDialog
        :is-open="dialog.reviewFormLink"
        :review-form-link="reviewFormLink"
        @close="dialog.reviewFormLink = false"
      />
    </v-row>
  </v-container>
</template>

<script>
import NoData from "@/components/ui/NoData";
import ViewHeader from "@/components/ui/ViewHeader.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import TechnicalDetailsDialog from "@/components/FacilityDetails/Dialog/TechnicalDetailsDialog.vue";
import ReviewFormLinkDialog from "@/components/FacilityDetails/Dialog/ReviewFormLinkDialog.vue";

import facilitiesService from "@/services/facilities";
import serviceHandler from "@/helpers/handlers/service";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { Permission } from "@/enums/permission";
import { permissionCheck } from "@/helpers/permission-check";
import { EventBus } from "@/plugins/event-bus";
import moment from "moment";
import cuid from "cuid";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import IntegrationStatusIndicator from "@/components/Permissions/IntegrationStatusIndicator.vue";
import MAP_INTEGRATIONS from "@/config/map-integrations";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import { mapGetters } from "vuex";

import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import { mapIntegrationsMixin } from "@/helpers/mixins/map-integrations";
export default {
  name: "FacilityDetails",
  components: {
    NoData,
    ViewHeader,
    SectionLoader,
    TechnicalDetailsDialog,
    IntegrationStatusIndicator,
    ReviewFormLinkDialog
  },
  mixins: [permissionsInfo, mapIntegrationsMixin],
  data() {
    return {
      moment,
      FEATURE_FLAG,
      viewTitle: this.$t("message.common.businessAddresses"),
      loading: {
        view: false
      },
      dialog: {
        removeFacility: false,
        technicalDetails: false,
        reviewFormLink: false
      },
      isOwner: true,
      isReadonly: false,
      gmbLocationName: "",
      integrations: [],
      active: 0,
      reviewFormLink: ""
    };
  },

  computed: {
    ...mapGetters({
      hasStoreLocator: "company/hasStoreLocator"
    }),
    canManageLocationData() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_MANAGE_LOCATION_DATA);
    },
    tabsWithAccess() {
      return this.tabs.filter(
        tab =>
          tab.access &&
          ("planRestrictions" in tab ? tab.planRestrictions : true)
      );
    },
    isDetailsRoute() {
      return this.$route.name === "facility-details";
    },
    enableStoreLocatorExtraInfo() {
      return (
        isFeatureFlagEnabled(FEATURE_FLAG.SHOW_STORE_LOCATOR_EXTRA_INFO) &&
        this.hasStoreLocator
      );
    }
  },

  async created() {
    this.tabs = [
      {
        id: cuid(),
        label: "message.common.details",
        route: {
          name: "facility-details",
          params: { id: this.$route.params.id }
        },
        access: permissionCheck(Permission.VIEW_FACILITIES_DATA),
        disableInReadonly: false
      },
      {
        id: cuid(),
        label: "message.externalChanges.list.title",
        route: {
          name: "facility-external-changes-list",
          params: { id: this.$route.params.id }
        },
        access: permissionCheck(Permission.VIEW_EXTERNAL_CHANGES),
        planRestrictions: planRestrictionsCheck(
          PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
        ),
        disableInReadonly: true,
        hasExternalChanges: false
      },
      {
        id: cuid(),
        label: "message.externalChanges.history.title",
        route: {
          name: "facility-external-changes-history",
          params: { id: this.$route.params.id }
        },
        access: permissionCheck(Permission.VIEW_EXTERNAL_CHANGES_HISTORY),
        planRestrictions: planRestrictionsCheck(
          PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
        ),
        disableInReadonly: true
      },
      {
        id: cuid(),
        label: "message.storeLocator.extraInfo.pageTitle",
        route: {
          name: "store-locator-config",
          params: { id: this.$route.params.id }
        },
        access: this.enableStoreLocatorExtraInfo,
        disableInReadonly: true
      }
    ];
    this.loading.view = true;
    EventBus.$on("facility-external-changes", this.setExternalChanges);
    await this.fetchFacilityData();
    this.loading.view = false;
  },

  destroyed() {
    EventBus.$off("facility-external-changes", this.setExternalChanges);
  },

  methods: {
    isFeatureFlagEnabled,
    setIntegrationsData(apiData) {
      const integrationsList = this.getIntegrationsList(apiData);

      if (!isFeatureFlagEnabled(FEATURE_FLAG.SHOW_INTEGRATIONS)) {
        this.integrations = integrationsList.filter(
          integration => integration.type === "google"
        );
        return;
      }
      this.integrations = integrationsList;
    },
    async fetchFacilityData() {
      const data = await serviceHandler(
        facilitiesService.getFacilityData(this.$route.params.id)
      );

      if (data) {
        if (data.maps_integration_status) {
          this.setIntegrationsData(data.maps_integration_status);
        }
        this.isOwner = data.facility_listings.is_owner;
        this.isReadonly = data.facility_listings.address_group === null;
        this.gmbLocationName = data.gmb_location_name || "";
        this.reviewFormLink = data.write_review_url;
        this.setExternalChanges(data.external_changes);
      }
    },
    async onDelete(id) {
      await serviceHandler(facilitiesService.deleteFacility(id));

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATIONS_DISCONNECT,
        location_id: id
      });

      this.dialog.removeFacility = false;
      this.$router.push({ name: "business-addresses" });
    },

    setExternalChanges(state) {
      const externalChangesListTab = this.tabs.find(tab =>
        Object.keys(tab).includes("hasExternalChanges")
      );
      externalChangesListTab.hasExternalChanges = state;
    },

    goToFacilitiesList() {
      if (this.$route.query.back) {
        this.$router.push({ path: this.$route.query.back });
      } else {
        this.$router.push({
          name: "business-addresses-list"
        });
      }
    },

    updateRouter(val) {
      this.$router.push(val);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables.scss";

.facility-details {
  &__card {
    width: 100%;
    position: relative;
  }

  &__map-card {
    margin-top: $spacing * 3;
    padding: $spacing * 4 $spacing * 6;
  }

  &__tab {
    position: relative;
    padding: 0 $spacing * 5;
  }

  &__tabs {
    border-bottom: 1px solid $gray-200;
  }

  .v-tabs .v-tab:first-of-type {
    margin-left: $spacing * 3;
  }

  &__wrapper {
    min-height: 350px;
    padding: $spacing * 2 $spacing * 4 $spacing * 4;
  }
}

.has-changes {
  position: absolute;
  width: 8px;
  height: 8px;
  top: 50%;
  right: 20px;
  border-radius: 50%;
  background-color: $lime;
  transform: translateY(-50%);
  animation: pulse-animation 2s infinite;
}

.facility-details-update-statuses-data-wrapper {
  @media #{map-get($display-breakpoints, 'md-and-up')} {
    margin-top: $spacing * 5;
  }
}

.facility-details-update-statuses-description {
  @include font-size(14px);
  line-height: 22px;
}

.facility-details-update-status {
  position: relative;
  padding-left: $spacing;
  display: flex;
  align-items: center;
}

.facility-details-update-status::before {
  content: "";
  background-color: var(--status-color);
  border-radius: 50%;
  width: 8px;
  height: 8px;
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
}

.update-statuses-header {
  @include font-size(14px);
  color: $gray-dark;
  font-weight: normal;
}
.update-status-data {
  color: $grey-darker;
  @include font-size(16px);
  font-weight: normal;
  line-height: 20px;
}

.update-status-data--name {
  font-weight: 500;
}
.update-status-data--date {
  @include font-size(14px);
  line-height: 22px;
}

.section:not(:first-child) {
  margin-top: $spacing * 4;
}

.section-title {
  @include font-size(16px);
  font-weight: 600;
  line-height: 20px;
  color: $grey-darker;
  margin-bottom: $spacing * 2;
  @include borderAccent($lime);
}

@keyframes pulse-animation {
  0% {
    box-shadow: 0 0 0 0px rgba($lime, 0.5);
  }
  100% {
    box-shadow: 0 0 0 10px rgba($lime, 0);
  }
}
</style>
